import { useState } from 'react';
import { Box, TextField, Popover, Button, InputAdornment, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { colors } from '@src/styles/colors';
import {
  calendarPopperSX,
  dropdownPaperSX,
  filterFieldSX,
  ghostButtonSX,
  monoLabelSX,
  primaryButtonSX,
} from '../scriptsStyles';

interface DateRangeFilterProps {
  label: string;
  startDate: Date | null;
  endDate: Date | null;
  onChange: (startDate: Date | null, endDate: Date | null) => void;
  minWidth?: number;
}

export const DateRangeFilter = ({ label, startDate, endDate, onChange, minWidth }: DateRangeFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [localStartDate, setLocalStartDate] = useState<Date | null>(startDate);
  const [localEndDate, setLocalEndDate] = useState<Date | null>(endDate);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChange(localStartDate, localEndDate);
    handleClose();
  };

  const handleClear = () => {
    setLocalStartDate(null);
    setLocalEndDate(null);
    onChange(null, null);
    handleClose();
  };

  const open = Boolean(anchorEl);

  const getDisplayValue = () => {
    if (!startDate && !endDate) return label;
    if (startDate && endDate) {
      return `${startDate.toLocaleDateString('pt-BR')} - ${endDate.toLocaleDateString('pt-BR')}`;
    }
    if (startDate) return `Desde ${startDate.toLocaleDateString('pt-BR')}`;
    if (endDate) return `Até ${endDate.toLocaleDateString('pt-BR')}`;
    return label;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <TextField
        size="small"
        value={getDisplayValue()}
        onClick={handleClick}
        placeholder={label}
        inputProps={{ 'aria-label': label }}
        sx={{
          minWidth: minWidth || 190,
          ...filterFieldSX,
          '& .MuiOutlinedInput-root': {
            ...filterFieldSX['& .MuiOutlinedInput-root'],
            cursor: 'pointer',
          },
          '& input': {
            cursor: 'pointer',
            color: startDate || endDate ? colors.primary.white : colors.brand.textMuted,
          },
        }}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonthOutlinedIcon sx={{ fontSize: 17, color: `${colors.brand.textMuted} !important` }} />
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{ sx: { ...dropdownPaperSX, marginTop: 1, p: 2 } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 300 }}>
          <Typography sx={monoLabelSX}>Período de criação</Typography>
          <DatePicker
            label="Data inicial"
            value={localStartDate}
            onChange={(newValue) => setLocalStartDate(newValue)}
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  '& .MuiPickersInputBase-root, & .MuiOutlinedInput-root': {
                    backgroundColor: colors.background.primary,
                    color: `${colors.primary.white} !important`,
                    borderRadius: '8px',
                  },
                  '& fieldset, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: `${colors.brand.line} !important`,
                  },
                  '& .Mui-focused fieldset, & .Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: `${colors.primary.yellow} !important`,
                  },
                  '& .MuiInputLabel-root, & .MuiFormLabel-root': {
                    color: colors.brand.textSecondary,
                    '&.Mui-focused': { color: colors.primary.yellow },
                  },
                  '& .MuiSvgIcon-root': { color: `${colors.brand.textSecondary} !important` },
                },
              },
              popper: { sx: calendarPopperSX },
            }}
          />
          <DatePicker
            label="Data final"
            value={localEndDate}
            onChange={(newValue) => setLocalEndDate(newValue)}
            minDate={localStartDate || undefined}
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  '& .MuiPickersInputBase-root, & .MuiOutlinedInput-root': {
                    backgroundColor: colors.background.primary,
                    color: `${colors.primary.white} !important`,
                    borderRadius: '8px',
                  },
                  '& fieldset, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: `${colors.brand.line} !important`,
                  },
                  '& .Mui-focused fieldset, & .Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: `${colors.primary.yellow} !important`,
                  },
                  '& .MuiInputLabel-root, & .MuiFormLabel-root': {
                    color: colors.brand.textSecondary,
                    '&.Mui-focused': { color: colors.primary.yellow },
                  },
                  '& .MuiSvgIcon-root': { color: `${colors.brand.textSecondary} !important` },
                },
              },
              popper: { sx: calendarPopperSX },
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleClear} sx={ghostButtonSX}>
              Limpar
            </Button>
            <Button variant="contained" disableElevation onClick={handleApply} sx={primaryButtonSX}>
              Aplicar
            </Button>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};
